import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import {
  AWS_HOSTED_UI_URL_APPLE,
  AWS_HOSTED_UI_URL_GOOGLE,
  AWS_REFRESH_TOKEN_INTERVAL,
  IS_MOBILE_APP,
} from '@/constants'
import {
  type AWSTokenResponse,
  cognitoLogout,
  exchangeCodeForToken,
  invalidateToken,
  refreshToken
} from '@/api/auth'
import type { AuthIdentityProvidersType, MeResponse, UserResponse } from '@/api/types/User'
import { NEEDS_INVITE_CODE } from '@/api/profile'
import toast from '@/utils/toast'
import { getMe, deleteMe, createIdentity } from '@/api/user'
import { getInviteFlowParams, clearInviteFlowParams } from '@/utils/inviteFlow'
import { clearUser, setUser } from '@/utils/sentry'
import { consumeNextState, saveCurrentState } from '@/utils/afterAuthRedirect'
import { currentOrganizationDecider, updateCurrentOrganization } from '@/utils/currentOrganizationPersister'
import { MobileAppBridge } from '@/utils/mobileAppActions'
import { getKeepMeLoggedIn } from '@/utils/keepMeLoggedIn'
import { initInactivityTracker } from '@/utils/userIdleDetection'

type MeOrganization = MeResponse['organizations'][number]

export interface AuthStoreTypes {
  credentials: AWSTokenResponse | undefined;
  user: UserResponse | undefined;
  organizations: MeResponse['organizations'] | undefined;
  currentOrganization: MeOrganization | undefined;
  isLoggedIn: boolean;
  hasOrganizations: boolean;
  needsInviteCode: boolean;
  loading: boolean;
  refreshTimer: ReturnType<typeof setTimeout> | undefined;
  setCredentials: (credentials: AWSTokenResponse) => void;
  changeOrganization: (organizationId: MeOrganization['id']) => void;
  initialize: (coldStart: boolean, inviteCode?: string, email?: string) => Promise<void>;
  tokenRefresh: () => Promise<void>;
  login: (code: string) => Promise<boolean>;
  logout: () => Promise<void>;
  deleteUser: () => Promise<boolean>;
}

const keysToPersist = ['credentials']
const LINK_IDENTITY_ERROR_TOAST_KEY = 'auth:link-identity-error-toast'

const defaultValues = {
  credentials: undefined,
  user: undefined,
  organizations: [],
  currentOrganization: undefined,
  isLoggedIn: false,
  hasOrganizations: false,
  needsInviteCode: false,
  loading: false,
  refreshTimer: undefined
}

export const startAuth = async (provider: 'apple' | 'google', connect?: boolean) => {
  saveCurrentState()

  if (connect) {
    sessionStorage.setItem(
      'auth:provider',
      provider === 'apple' ? 'SignInWithApple' : 'Google'
    )
  } else {
    sessionStorage.removeItem('auth:provider')
  }

  if (IS_MOBILE_APP) {
    // mobile
    const { code } = await MobileAppBridge.signIn(provider)
    if (code) {
      window.location.href = window.location.origin + '/?code=' + code
    }
  } else {
    // web
    window.location.href = provider === 'apple'
      ? AWS_HOSTED_UI_URL_APPLE
      : AWS_HOSTED_UI_URL_GOOGLE
  }
}

const useAuthStore = create<AuthStoreTypes>()(
  persist(
    (set, get) => ({
      ...defaultValues,

      setCredentials: (credentials: AWSTokenResponse) => {
        set({ credentials })
      },

      initialize: async (coldStart, inviteCode = undefined, email = undefined) => {
        // Check for pending link identity error from OAuth redirect
        const linkIdentityError = sessionStorage.getItem(LINK_IDENTITY_ERROR_TOAST_KEY)
        if (linkIdentityError) sessionStorage.removeItem(LINK_IDENTITY_ERROR_TOAST_KEY)

        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        if (code && coldStart) {
          // Remove code from url params
          window.history.replaceState({}, document.title, window.location.pathname)
          await get().login(code)
          return
        }

        set({ loading: true })
        const creds = get().credentials
        if (!creds) {
          get().logout()
          return
        }

        if (coldStart && creds.refresh_token) {
          // Cold start means the app just started with old credentials
          // We need to refresh the access token
          try {
            await get().tokenRefresh()
          } catch {
            // Token refresh failed
            await get().logout()
            return
          }
        }

        // Resolve invite params: explicit args take priority, then check localStorage
        // (localStorage is populated by AuthSetup/Login before SSO redirects)
        let meParams: { inviteCode?: string; inviteToken?: string; email?: string } | undefined
        if (inviteCode || email) {
          meParams = { inviteCode, email }
        } else {
          const saved = getInviteFlowParams()
          if (saved) meParams = saved
        }

        try {
          const updatedUser = await getMe(meParams)
          if (updatedUser.success) {
            // Invite params consumed successfully, clear them from localStorage
            clearInviteFlowParams()
            // Success, credentials are valid, Update the store
            set({
              user: updatedUser.data.user,
              organizations: updatedUser.data.organizations,
              currentOrganization: currentOrganizationDecider(updatedUser.data.organizations),
              hasOrganizations: (updatedUser.data.organizations?.length ?? 0) > 0,
              needsInviteCode: false,
              isLoggedIn: true,
              loading: false
            })

            // Update Sentry user info
            setUser({
              id: updatedUser.data.user.id,
              email: updatedUser.data.user.email,
              isVerified: updatedUser.data.user.isVerified,
              loggedInAt: new Date().toISOString()
            })

            // Redirect to saved path if any
            const nextPath = consumeNextState()
            if (nextPath) window.location.href = nextPath

            // Set up a timer to refresh the token before it expires
            const oldTimer = get().refreshTimer
            if (oldTimer) clearInterval(oldTimer)
            const timer = setInterval(async () => {
              try {
                await get().tokenRefresh()
              } catch {
                // Token refresh failed
                await get().logout()
                return
              }
            }, AWS_REFRESH_TOKEN_INTERVAL)
            set({ refreshTimer: timer })

            // Initialize inactivity tracker
            initInactivityTracker(get().logout)

            // Show pending link identity error if any
            if (linkIdentityError) toast.error(linkIdentityError)
          } else {
            set({ loading: false })
            toast.error('Failed to fetch user data')
          }
        } catch (error: any) {
          set({ loading: false })

          // Check if error message is "NEEDS_INVITE_CODE"
          if (error.message === NEEDS_INVITE_CODE) {
            set({ needsInviteCode: true })
            return
          }

          // Invite params failed for a reason other than NEEDS_INVITE_CODE
          // (e.g. expired token, invalid token) — clear them so they don't
          // block future logins
          clearInviteFlowParams()

          // eslint-disable-next-line no-console
          console.error('💥 Initialize failed', error)
          if (get().needsInviteCode) {
            toast.error(error.cause || 'Failed to validate invite code', '', true)
          } else {
            await get().logout()
          }
        }
      },

      changeOrganization: (organizationId: MeOrganization['id']) => {
        const currentOrganization = get().currentOrganization
        if (currentOrganization?.id === organizationId) return
        const organizations = get().organizations || []
        const newOrg = organizations.find(org => org.id === organizationId)
        if (newOrg) {
          // Update key in localStorage
          updateCurrentOrganization(newOrg.id)
          // Reload the app to reflect the organization change
          window.location.href = '/'
        } else {
          toast.error('Organization not found')
        }
      },

      tokenRefresh: async () => {
        const creds = get().credentials
        if (!creds?.refresh_token) {
          throw new Error('No refresh token available')
        }
        const newCreds = await refreshToken(creds.refresh_token)
        set({ credentials: newCreds })
      },

      login: async (code: string) => {
        set({ loading: true })
        try {
          const provider = sessionStorage.getItem('auth:provider') as AuthIdentityProvidersType | null
          const tokenResponse = await exchangeCodeForToken(code)

          if (!provider && tokenResponse.access_token) set({ credentials: tokenResponse })

          if (provider && tokenResponse.id_token) {
            try {
              await createIdentity({
                idToken: tokenResponse.id_token,
                provider,
              })
            } catch (error: unknown) {
              const linkIdentityError = error as {
                statusCode?: number
                message?: string
              }
              const statusCode = linkIdentityError?.statusCode
              const errorMessage = statusCode === 409 && linkIdentityError?.message
                ? linkIdentityError.message
                : 'Failed to link identity'

              const nextPath = consumeNextState()
              if (nextPath) {
                sessionStorage.setItem(LINK_IDENTITY_ERROR_TOAST_KEY, errorMessage)
                set({ loading: false })
                window.location.href = nextPath
                return false
              }

              set({ loading: false })
              toast.error(errorMessage)
              return false
            } finally {
              sessionStorage.removeItem('auth:provider')
            }
          }

          await get().initialize(false)
          if (code) {
            // After exchanging code for token, call the logout endpoint
            // to remove all cookies set by Cognito Hosted UI
            // This will allow to re-login with a different google/apple account
            // and the account will stay logged in because the access_token is saved
            cognitoLogout() // -> Yes, this is a workaround, but it works
          }
          return true
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('💥 Auth failed', error)
        }
        return false
      },

      logout: async () => {
        const oldTimer = get().refreshTimer
        if (oldTimer) clearInterval(oldTimer)
        const oldCreds = get().credentials
        set(defaultValues)
        clearUser()
        if (oldCreds?.refresh_token) {
          invalidateToken(oldCreds?.refresh_token).catch(
            () => null
          )
        }
      },

      deleteUser: async () => {
        set({ loading: true })
        try {
          const res = await deleteMe()
          if (!res.success) {
            const message =
              res.statusCode === 403
                ? res.message || 'Failed to delete user'
                : 'Failed to delete user'
            toast.error(message)
            return false
          }
          await get().logout()
          toast.success('Your account has been deleted successfully.')
          return true
        } catch (error) {
          const err = error as { message?: string }
          toast.error(err?.message || 'Failed to delete user')
          return false
        } finally {
          set({ loading: false })
        }
      }
    }),
    {
      name: 'aws-auth-storage',
      storage: createJSONStorage(() => (getKeepMeLoggedIn() ? localStorage : sessionStorage)),
      partialize: state =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => keysToPersist.includes(key))
        ),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (!state || error) return
          if (state) state.initialize(true)
        }
      }
    }
  )
)

export const useCurrentOrgId = () => {
  return useAuthStore(state => state.currentOrganization?.id)
}

export default useAuthStore
